import { Fragment } from "react";
import { Table } from "react-bootstrap";
import type { EventEntrants } from "../util/tournamentStats";
import type { PlayerLookupResult } from "../../shared/types";
import { StockIcons } from "./StockIcon";

interface TournamentPlayersProps {
  events: EventEntrants[];
  /** Raw entrant name → canonical player publicId, or null if unresolved. */
  playerLookup: PlayerLookupResult;
  onOpenPlayer: (publicId: string) => void;
}

// One table per bracket (pools, main, amateur, …) with the characters each
// entrant played and their win–loss record. Records cover only sets we have
// replays for - not official start.gg placement.
export default function TournamentPlayers({
  events,
  playerLookup,
  onOpenPlayer,
}: TournamentPlayersProps) {
  const hasPlayers = events.some((e) => e.entrants.length > 0);
  // A single-bracket tournament reads cleaner without a redundant heading.
  const showEventLabels = events.length > 1;

  return (
    <div className="bg-body-tertiary rounded p-3">
      <h6 className="border-bottom pb-1">Players</h6>
      {!hasPlayers ? (
        <p className="text-muted small">No players found</p>
      ) : (
        events.map((event) => (
          <Fragment key={event.eventName}>
            {showEventLabels && (
              <div className="fw-semibold small text-body-secondary mt-3 mb-1">
                {event.eventName}
              </div>
            )}
            <Table hover responsive size="sm" className="align-middle mb-0 overview-players-table">
              <thead>
                <tr>
                  <th style={{ width: "2.5rem" }} className="text-end">
                    #
                  </th>
                  <th>Player</th>
                  <th>Characters</th>
                  <th className="text-end">W–L</th>
                </tr>
              </thead>
              <tbody>
                {event.entrants.map((e, i) => {
                  const publicId = playerLookup[e.name];
                  return (
                    <tr key={e.name}>
                      <td className="text-end text-muted">{i + 1}</td>
                      <td className="fw-medium text-truncate" style={{ maxWidth: "12rem" }}>
                        {publicId ? (
                          <a
                            href={`/player/${publicId}`}
                            onClick={(ev) => {
                              if (
                                ev.metaKey ||
                                ev.ctrlKey ||
                                ev.shiftKey ||
                                ev.altKey ||
                                ev.button !== 0
                              )
                                return;
                              ev.preventDefault();
                              onOpenPlayer(publicId);
                            }}
                            className="link-body-emphasis link-underline-opacity-0 link-underline-opacity-75-hover"
                            title="Open player page"
                          >
                            {e.name}
                          </a>
                        ) : (
                          e.name
                        )}
                      </td>
                      <td>
                        <StockIcons characters={e.characters} size={24} />
                      </td>
                      <td className="text-end text-nowrap">
                        <span className="text-success">{e.wins}</span>
                        <span className="text-muted">–</span>
                        <span className="text-danger">{e.losses}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Fragment>
        ))
      )}
    </div>
  );
}
